import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Board, HOURS, Series, fmtHour } from "@/features/forecast/board";
import cls from "./HourChart.module.scss";

/* Геометрия — как в прототипе (buildLine). */
const CW = 336;
const CH = 132;
const PL = 6;
const PR = 6;
const PT = 12;
const PB = 6;

type Pt = [number, number];

/** Катмулл-Ром → Безье: сглаженная кривая через точки. */
function smooth(pts: Pt[]): string {
  if (pts.length < 2) return "";
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    d += `C${(p1[0] + (p2[0] - p0[0]) / 6).toFixed(1)},${(p1[1] + (p2[1] - p0[1]) / 6).toFixed(1)} `;
    d += `${(p2[0] - (p3[0] - p1[0]) / 6).toFixed(1)},${(p2[1] - (p3[1] - p1[1]) / 6).toFixed(1)} `;
    d += `${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
}

interface Props {
  board: Board;
  hour: number;
  /** `null` — вернуться к текущему часу. */
  onHour: (hour: number | null) => void;
}

export const HourChart: React.FC<Props> = ({ board, hour, onHour }) => {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [dragging, setDragging] = useState(false);

  const { gx, gy, lead, paths } = useMemo(() => {
    const live = board.series.filter((s) => !s.dead);
    const values = live.flatMap((s) => s.v.filter((v): v is number => v != null));
    const peak = Math.max(...(values.length ? values : [300]), 1);
    const low = Math.min(...(values.length ? values : [0]));
    const maxY = peak * 1.06;
    const minY = Math.max(0, low - (peak - low) * 0.25);

    const x = (i: number) => PL + ((CW - PL - PR) * i) / (HOURS - 1);
    const y = (v: number) => {
      const k = Math.max(0, Math.min(1, (v - minY) / (maxY - minY || 1)));
      return PT + (CH - PT - PB) * (1 - k);
    };

    /*
     * Сплошная — до «сейчас», точечный пунктир — после. Граница общая для всех
     * линий (`board.nowIndex`), а не своя у каждой: полнота факта у тарифов
     * разная, и с отдельными границами пунктир у одного начинался раньше
     * маркера, а у другого позже — график читался как ошибка.
     */
    const split = (s: Series) => {
      const idx = s.v.map((v, i) => (v != null ? i : -1)).filter((i) => i >= 0);
      if (!idx.length) return { solid: [] as Pt[], dotted: [] as Pt[] };
      const edge = board.nowIndex;
      const solid = idx.filter((i) => i <= edge).map((i): Pt => [x(i), y(s.v[i] as number)]);
      const dotted = idx.filter((i) => i >= edge).map((i): Pt => [x(i), y(s.v[i] as number)]);
      return { solid, dotted };
    };

    const leadSeries = live
      .filter((s) => s.v[board.nowIndex] != null)
      .reduce<Series | null>(
        (a, b) => (a && (a.v[board.nowIndex] as number) > (b.v[board.nowIndex] as number) ? a : b),
        null
      );

    return {
      gx: x,
      gy: y,
      lead: leadSeries,
      paths: board.series.map((s) => ({ s, ...split(s) })),
    };
  }, [board]);

  const hourFromEvent = (e: React.PointerEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return hour;
    const px = ((e.clientX - rect.left) / rect.width) * CW - PL;
    const step = (CW - PL - PR) / (HOURS - 1);
    return Math.max(0, Math.min(HOURS - 1, Math.round(px / step)));
  };

  const rows = [...board.series].sort(
    (a, b) => (b.dead ? -1 : (b.v[hour] ?? -1)) - (a.dead ? -1 : (a.v[hour] ?? -1))
  );
  const tipRight = gx(hour) / CW <= 0.5;
  /* Заливка только под лидером «сейчас» и только под фактической частью. */
  const leadSolid = lead ? paths.find((p) => p.s === lead)?.solid ?? [] : [];
  const leadFill =
    leadSolid.length >= 2
      ? `${smooth(leadSolid)}L${leadSolid[leadSolid.length - 1][0].toFixed(1)},${CH - PB} L${leadSolid[0][0].toFixed(1)},${CH - PB} Z`
      : "";

  const axisLabels = [0, 6, 12, 18, 23].map((i) => String(board.hours[i]).padStart(2, "0"));

  return (
    <>
      <div className={cls.wrap}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${CW} ${CH}`}
          role="img"
          aria-label={t("compare.perHour")}
          onPointerDown={(e) => {
            setDragging(true);
            (e.target as Element).setPointerCapture?.(e.pointerId);
            onHour(hourFromEvent(e));
          }}
          onPointerMove={(e) => {
            if (dragging || e.pointerType === "mouse") onHour(hourFromEvent(e));
          }}
          onPointerUp={() => setDragging(false)}
          onPointerCancel={() => setDragging(false)}
          onPointerLeave={(e) => {
            // Мышь ушла с графика — курсор возвращается к «сейчас», иначе на
            // десктопе он залипает на случайном часе. На тач-экранах это не срабатывает.
            if (e.pointerType === "mouse" && !dragging) onHour(null);
          }}
          style={{ cursor: "ew-resize", touchAction: "none" }}
        >
          {lead && (
            <defs>
              <linearGradient id="leadFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lead.color} stopOpacity=".26" />
                <stop offset="100%" stopColor={lead.color} stopOpacity="0" />
              </linearGradient>
            </defs>
          )}
          {lead && leadFill && <path d={leadFill} fill="url(#leadFill)" />}
          {paths.map(({ s, solid, dotted }) => (
            <g key={s.id} opacity={s.dead ? 0.35 : 1}>
              <path
                fill="none"
                stroke={s.color}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                d={smooth(solid)}
              />
              <path
                fill="none"
                stroke={s.color}
                strokeWidth="2.4"
                strokeDasharray="1 5"
                strokeLinecap="round"
                opacity={0.55}
                d={smooth(dotted)}
              />
            </g>
          ))}
          <line
            stroke="rgba(255,255,255,.16)"
            strokeWidth="1"
            x1={gx(hour)}
            x2={gx(hour)}
            y1={PT}
            y2={CH - PB}
          />
          {board.series.map(
            (s) =>
              s.v[hour] != null && (
                <circle
                  key={s.id}
                  cx={gx(hour)}
                  cy={gy(s.v[hour] as number)}
                  r="3.4"
                  fill={s.color}
                  stroke="var(--bg)"
                  strokeWidth="2"
                  opacity={s.dead ? 0.35 : 1}
                />
              )
          )}
        </svg>
        <div
          className={cls.tip}
          style={tipRight ? { right: 2, left: "auto" } : { left: 2, right: "auto" }}
        >
          {rows.map((r) => (
            <div className={cls.tr} key={r.id}>
              <i style={{ background: r.color, opacity: r.dead ? 0.4 : 1 }} />
              <span className={cls.n}>{r.label}</span>
              {r.dead || r.v[hour] == null ? (
                <b className={cls.nd}>{t("compare.noData").toLowerCase()}</b>
              ) : (
                <b>{r.v[hour]} ₽</b>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={cls.axis}>
        {axisLabels.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i}>{l}</span>
        ))}
      </div>
      <div className={cls.legend}>
        {board.series.map((s) => (
          <span className={cls.it} key={s.id}>
            <i style={{ background: s.color }} />
            {s.label}
            {s.dead && <b className={cls.dead}>{t("compare.noData").toLowerCase()}</b>}
          </span>
        ))}
        <span className={cls.it}>
          <i className={cls.now} />
          {t("compare.factUntil", { time: fmtHour(board.hours[board.nowIndex]) })}
        </span>
      </div>
    </>
  );
};
